/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { HeartPulse, KeyRound, LogOut } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';

import { useMe } from '../../lib/queries/me.js';
import { authStore } from '../../lib/stores/auth-store.js';
import { uiStore } from '../../lib/stores/ui-store.js';
import { cn } from '../../lib/utils/cn.js';
import { ChangePasswordForm } from '../account/change-password-form.js';
import { Modal } from '../ui/modal.js';

import { type AddAction, TopBar } from './top-bar.js';

import type { NavItem } from './nav-config.js';

type PortalKind = 'CLINIC_ADMIN' | 'THERAPIST' | 'PARENT' | 'FINANCE' | 'STAFF';

interface PortalShellProps {
  readonly portal: PortalKind;
  readonly nav: readonly NavItem[];
}

const PORTAL_LABEL: Readonly<Record<PortalKind, string>> = {
  CLINIC_ADMIN: 'Admin Portal',
  THERAPIST: 'Therapist Portal',
  PARENT: 'Patient Portal',
  FINANCE: 'Finance Portal',
  STAFF: 'Staff Console',
};

/** Every portal-tinted surface uses the `--accent` CSS var (set per portal on
 *  <html> via `data-portal`), so each portal matches its Figma palette. The
 *  gradient mirrors the Figma brand chip + active-nav treatment. */
const ACCENT_BG = 'bg-[var(--accent)]';
const ACCENT_GRADIENT = 'bg-[image:var(--accent-gradient)]';

const ADD_ACTIONS: Readonly<Record<PortalKind, readonly AddAction[]>> = {
  CLINIC_ADMIN: [
    { labelKey: 'admin.clients.add', to: '/admin/clients?new=1' },
    { labelKey: 'admin.therapists.add', to: '/admin/therapists?new=1' },
    { labelKey: 'admin.sessions.schedule', to: '/admin/sessions?new=1' },
    { labelKey: 'finance.invoices.add', to: '/admin/finance?new=1' },
  ],
  THERAPIST: [{ labelKey: 'therapist.schedule.add', to: '/therapist/schedule?new=1' }],
  PARENT: [],
  FINANCE: [{ labelKey: 'finance.invoices.add', to: '/finance/invoices?new=1' }],
  STAFF: [],
};

function initials(name: string, email: string): string {
  const source = name.trim() !== '' ? name : email;
  const parts = source
    .trim()
    .split(/\s+/)
    .filter((p) => p.length > 0);
  if (parts.length === 0) {
    return '?';
  }
  const first = parts[0]?.[0] ?? '';
  const second = parts[1]?.[0] ?? '';
  return `${first}${second}`.toUpperCase().slice(0, 2);
}

const UserCard = observer(function UserCard({
  accent,
  onLogout,
}: {
  readonly accent: string;
  readonly onLogout: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const me = useMe();
  const [changeOpen, setChangeOpen] = useState(false);
  const name = me.data?.fullName !== undefined && me.data.fullName !== '' ? me.data.fullName : '—';
  const email = me.data?.email ?? '';
  const openChange = useCallback(() => {
    setChangeOpen(true);
  }, []);
  const closeChange = useCallback(() => {
    setChangeOpen(false);
  }, []);
  return (
    <div className="flex items-center gap-2 border-t border-slate-200 p-4 dark:border-slate-700">
      <span
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white',
          accent,
        )}
      >
        {initials(me.data?.fullName ?? '', email)}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-slate-800 dark:text-white">{name}</p>
        <p className="truncate text-xs text-slate-500 dark:text-slate-400">{email}</p>
      </div>
      <button
        type="button"
        onClick={openChange}
        className="focus-ring rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
        aria-label={t('account.changePassword.title')}
      >
        <KeyRound className="h-4 w-4" aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={onLogout}
        className="focus-ring rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
        aria-label={t('common.signOut')}
      >
        <LogOut className="h-4 w-4" aria-hidden="true" />
      </button>
      <Modal
        open={changeOpen}
        onOpenChange={setChangeOpen}
        title={t('account.changePassword.title')}
        description={t('account.changePassword.subtitle')}
        closeLabel={t('common.cancel')}
      >
        <ChangePasswordForm onDone={closeChange} />
      </Modal>
    </div>
  );
});

/**
 * Shared portal layout — left sidebar + global top bar + content outlet,
 * mirroring the Figma grammar across all four customer portals and the staff
 * console (neutral-dark accent). Dark mode is driven by the `dark` class that
 * `uiStore` toggles on `<html>`.
 */
export const PortalShell = observer(function PortalShell({
  portal,
  nav,
}: PortalShellProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Drive the portal accent palette (Figma-exact per role) via a `data-portal`
  // attribute on <html>; CSS vars on :root then tint buttons, nav, pills, and
  // any modal portaled to <body>.
  useEffect(() => {
    document.documentElement.dataset.portal = portal;
    return () => {
      delete document.documentElement.dataset.portal;
    };
  }, [portal]);

  // The Super Admin staff console is a neutral-dark internal tool (no Figma —
  // CLAUDE.md §1): force dark mode while it is mounted so it reads distinctly
  // from the light customer portals, restoring the user's chosen theme on exit.
  useEffect(() => {
    if (portal !== 'STAFF') {
      return () => undefined;
    }
    const root = document.documentElement;
    root.classList.add('dark');
    return () => {
      root.classList.toggle('dark', uiStore.theme === 'dark');
    };
  }, [portal]);

  const handleLogout = useCallback(() => {
    authStore.signOut();
    void navigate(portal === 'STAFF' ? '/staff/login' : '/');
  }, [navigate, portal]);

  // RBAC: system roles keep their full nav; custom roles see only granted modules.
  const visibleNav = nav.filter((item) => authStore.canSeeModule(item.module));

  const navLinkClass = useCallback(
    ({ isActive }: { isActive: boolean }) =>
      cn(
        'focus-ring flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
        isActive
          ? cn('text-white', ACCENT_GRADIENT)
          : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700',
      ),
    [],
  );

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-slate-950">
      <aside
        className="flex w-64 shrink-0 flex-col border-r border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900"
        aria-label={`${PORTAL_LABEL[portal]} navigation`}
      >
        <div className="flex items-center gap-3 px-5 py-5">
          <span
            className={cn(
              'flex h-10 w-10 items-center justify-center rounded-xl text-white',
              ACCENT_GRADIENT,
            )}
          >
            <HeartPulse className="h-5 w-5" aria-hidden="true" />
          </span>
          <div>
            <p className="text-base font-bold leading-tight text-slate-900 dark:text-white">
              Therapy<span className="font-light text-slate-400"> OS</span>
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400">{PORTAL_LABEL[portal]}</p>
          </div>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-2">
          {visibleNav.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path.split('/').length <= 2}
              className={navLinkClass}
            >
              <item.icon className="h-4 w-4" aria-hidden="true" />
              {t(`nav.${item.labelKey}`)}
            </NavLink>
          ))}
        </nav>

        <UserCard accent={ACCENT_BG} onLogout={handleLogout} />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar nav={visibleNav} addActions={ADD_ACTIONS[portal]} forcedDark={portal === 'STAFF'} />
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
});
